import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarBlank, ArrowRight, ArrowsLeftRight, X, SpinnerGap, CheckCircle } from '@phosphor-icons/react';

/*
  MoveDayPopover
  --------------
  Compact 📅 popover attached to a PlanDayCard header. Lets the user:
    • Shift the entire day's menu ±1 / ±2 / ±7 days
    • Pick an explicit target date via calendar
    • If target date already has a plan → offers to Swap the two dates

  Rendered via React Portal so it never gets clipped by the day-card border.

  Props:
    api       — axios instance
    plan      — the plan doc (uses plan.plan_id + plan.date)
    onDone    — called on success (usually onUpdate)
*/

const SHIFTS = [
  { d: -7, label: '−7d', color: 'slate' },
  { d: -1, label: '−1d', color: 'slate' },
  { d: 1, label: '+1d', color: 'orange' },
  { d: 2, label: '+2d', color: 'orange' },
  { d: 7, label: '+7d', color: 'orange' },
];

export default function MoveDayPopover({ api, plan, onDone }) {
  const [open, setOpen] = useState(false);
  const [targetDate, setTargetDate] = useState(plan?.date || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [conflict, setConflict] = useState(null); // {message, target_date}
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => { setTargetDate(plan?.date || ''); }, [plan?.date]);

  // Position the popover next to the button
  useEffect(() => {
    if (!open || !btnRef.current) return;
    const compute = () => {
      const rect = btnRef.current.getBoundingClientRect();
      const w = 288; // ~w-72
      let left = rect.right - w;
      if (left < 8) left = 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow < 320 && rect.top > 320 ? rect.top - 320 + 6 : rect.bottom + 6;
      setPos({ top, left });
    };
    compute();
    const onDown = (e) => {
      const inBtn = btnRef.current && btnRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inBtn && !inMenu) close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setBusy(false);
    setError('');
    setSuccess('');
    setConflict(null);
  };

  const doMove = async (payload) => {
    setBusy(true);
    setError('');
    setSuccess('');
    setConflict(null);
    try {
      const r = await api.post(`/meals/plans/${plan.plan_id}/move`, payload);
      const act = r.data?.action;
      if (act === 'swapped') {
        setSuccess(`Swapped ${r.data.from_date} ↔ ${r.data.to_date}`);
      } else if (act === 'moved') {
        setSuccess(`Moved to ${r.data.to_date}`);
      } else if (r.data?.unchanged) {
        setSuccess('Already on that date');
      }
      onDone && onDone();
      // Auto-close shortly after success so the user sees confirmation
      setTimeout(() => close(), 900);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // 409 with structured detail = date conflict
      if (e?.response?.status === 409 && detail && typeof detail === 'object') {
        setConflict({
          message: detail.message || 'That date already has a plan.',
          target_date: detail.existing_date || payload.target_date,
          existing_plan_id: detail.existing_plan_id,
          pendingPayload: payload,
        });
      } else {
        setError(typeof detail === 'string' ? detail : detail?.message || 'Move failed. Try again.');
      }
    }
    setBusy(false);
  };

  const doSwap = async () => {
    if (!conflict?.pendingPayload) return;
    await doMove({ ...conflict.pendingPayload, on_conflict: 'swap' });
  };

  const popover = open ? (
    <div
      ref={menuRef}
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: 288, zIndex: 9999 }}
      className="rounded-xl border border-purple-500/40 bg-neutral-900/95 backdrop-blur p-3 shadow-2xl text-left"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      data-testid="move-day-popover"
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] uppercase tracking-wider font-bold text-purple-300 inline-flex items-center gap-1">
          <CalendarBlank size={12} weight="fill" /> Move / swap day
        </p>
        <button onClick={close} className="text-neutral-500 hover:text-white p-0.5" title="Close">
          <X size={11} weight="bold" />
        </button>
      </div>

      {/* Success confirmation */}
      {success && (
        <div className="rounded-lg p-2 mb-2 text-[11px] text-emerald-200 inline-flex items-center gap-1.5"
          style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
          data-testid="move-day-success">
          <CheckCircle size={12} weight="fill" /> {success}
        </div>
      )}

      {/* Conflict resolution UI */}
      {conflict && !success && (
        <div className="rounded-lg p-2 mb-2 text-[11px] text-amber-200"
          style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}
          data-testid="move-day-conflict">
          <p className="font-semibold mb-1">A plan already exists on {conflict.target_date}.</p>
          <p className="text-neutral-400 mb-2">Swap the two dates? Both menus will exchange.</p>
          <div className="flex gap-1">
            <button
              onClick={doSwap}
              disabled={busy}
              data-testid="move-day-swap-confirm"
              className="flex-1 py-1 rounded-lg text-[11px] font-bold bg-amber-500/25 text-amber-200 border border-amber-500/50 hover:bg-amber-500/40 disabled:opacity-40 inline-flex items-center justify-center gap-1"
            >
              <ArrowsLeftRight size={12} weight="bold" /> Swap dates
            </button>
            <button
              onClick={() => { setConflict(null); }}
              className="px-3 py-1 rounded-lg text-[11px] font-semibold text-neutral-400 hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!success && !conflict && (
        <>
          {/* Quick shift buttons */}
          <p className="text-[9px] uppercase tracking-wider font-bold text-neutral-500 mb-1">Quick shift</p>
          <div className="flex flex-wrap gap-1 mb-2.5">
            {SHIFTS.map(s => (
              <button
                key={s.d}
                onClick={() => doMove({ shift_days: s.d })}
                disabled={busy}
                data-testid={`move-day-shift-${s.d}`}
                className={`px-2 py-1 rounded-lg text-[11px] font-bold border transition-colors disabled:opacity-40 ${s.color === 'orange'
                  ? 'bg-[#FF6B35]/10 text-[#FF6B35] border-[#FF6B35]/30 hover:bg-[#FF6B35]/25'
                  : 'bg-white/5 text-neutral-300 border-white/10 hover:bg-white/10 hover:text-white'}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Date picker */}
          <p className="text-[9px] uppercase tracking-wider font-bold text-neutral-500 mb-1">Move to specific date</p>
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              disabled={busy}
              data-testid="move-day-date-input"
              className="flex-1 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[11px] text-white outline-none focus:border-purple-500/40"
            />
            <button
              onClick={() => doMove({ target_date: targetDate })}
              disabled={busy || !targetDate || targetDate === plan?.date}
              data-testid="move-day-go"
              className="px-2 py-1.5 rounded-lg text-[11px] font-bold bg-purple-500/20 text-purple-200 border border-purple-500/40 hover:bg-purple-500/40 disabled:opacity-40 inline-flex items-center gap-1"
            >
              {busy ? <SpinnerGap size={11} className="animate-spin" /> : <ArrowRight size={11} weight="bold" />}
              Go
            </button>
          </div>

          <p className="text-[10px] text-neutral-500 mt-2 leading-snug">
            All chips, cook progress, ratings & staff menu move with the day.
          </p>
        </>
      )}

      {error && !conflict && (
        <div className="mt-2 rounded-lg p-2 text-[11px] text-red-300" style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)' }}
          data-testid="move-day-error">
          {error}
        </div>
      )}
    </div>
  ) : null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        data-testid={`move-day-btn-${plan?.plan_id || 'x'}`}
        title="Move or swap this day"
        className={`p-1 rounded-lg transition-colors ${open
          ? 'bg-purple-500/30 text-purple-200'
          : 'bg-white/5 text-neutral-400 hover:bg-white/10 hover:text-purple-300'}`}
      >
        <CalendarBlank size={12} weight="fill" />
      </button>
      {typeof document !== 'undefined' && popover ? createPortal(popover, document.body) : null}
    </>
  );
}
